// Iter-207 — <PageContainer> primitive + canonical width constants.
// Iter-402 — Added narrow (760) + content (960) tiers, sourced from
// the design-token CSS variables defined in styles/tokens.css.
//
// CarSavr's in-page editorial stripes all use the same horizontal
// container: 1200px max, centred, with `px-6 lg:px-10` gutters. That
// literal appeared in ~150 places across the codebase, which made it
// trivial for a new section to drift on width or padding. This
// primitive publishes the canonical class string so new code can
// reach for the constant instead of re-typing the literal and the
// width is locked in one place forever.
//
// Five width tiers ship today:
//   - NARROW_PAGE_CONTAINER  —  760, single-column articles / forms
//   - CONTENT_PAGE_CONTAINER —  960, editorial focused reading
//   - PAGE_CONTAINER         — 1200, the editorial default
//   - WIDE_PAGE_CONTAINER    — 1400, page header + banners
//   - FULL_PAGE_CONTAINER    — 1600, dashboards / wide tables
//
// `<PageContainer>` itself is an optional helper. For most call sites
// you'll just want to compose the constant into an existing className
// (e.g. `className={`${PAGE_CONTAINER} py-16 lg:py-20`}`). Use the
// component when you specifically want a one-line wrapper with no
// extra styling on top.

export const NARROW_PAGE_CONTAINER = "max-w-narrow mx-auto px-6 lg:px-10";
export const CONTENT_PAGE_CONTAINER = "max-w-content mx-auto px-6 lg:px-10";
export const PAGE_CONTAINER = "max-w-[1200px] mx-auto px-6 lg:px-10";
export const WIDE_PAGE_CONTAINER =
  "max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10";
export const FULL_PAGE_CONTAINER = "max-w-full mx-auto px-4 sm:px-6 lg:px-10";

const WIDTH_CLASS = {
  narrow:  NARROW_PAGE_CONTAINER,
  content: CONTENT_PAGE_CONTAINER,
  default: PAGE_CONTAINER,
  wide:    WIDE_PAGE_CONTAINER,
  full:    FULL_PAGE_CONTAINER,
};

export default function PageContainer({
  as: Tag = "div",
  width = "default",
  className = "",
  children,
  ...rest
}) {
  const base = WIDTH_CLASS[width] || PAGE_CONTAINER;
  return (
    <Tag {...rest} className={`${base} ${className}`.trim()}>
      {children}
    </Tag>
  );
}
